import { inject, Injectable } from "@angular/core";
import { FuseNavigation } from "@fuse/types";
import { AppConsts } from "@shared/AppConsts";
import { AppSessionService } from "@vadmin/account/auth";
import { EditionNames } from "app/wapps/system/subscription/edition-names";
import {
    NavigationAppManagementModules,
    NavigationAppManagementSettings,
    NavigationConfigChildGrowthCurve,
    NavigationConfigDocumentTemplates,
    NavigationConfigGeneralInfo,
    NavigationConfigHealthCareUnits,
    NavigationConfigHealthInsurance,
    NavigationConfigHealthProcedures,
    NavigationConfigHeathEngage,
    NavigationConfigMedicalRecord,
    NavigationConfigPatient,
    NavigationConfigProfessionalProcedures,
    NavigationConfigSchedule,
    NavigationConfigTranscription,
    NavigationDashboardItem,
    NavigationFinancial,
    NavigationFinancialCashFlow,
    NavigationFinancialConfig,
    NavigationFinancialExpenses,
    NavigationFinancialRevenues,
    NavigationItemAppManagement,
    NavigationItemClinicSettings,
    NavigationItemGlobalStats,
    NavigationItemHealthDoctors,
    NavigationItemHealthPatients,
    NavigationItemMarketingManagement,
    NavigationItemPay,
    NavigationItemProfile,
    NavigationItemScheduleCalendar,
    NavigationItemSettings,
    NavigationItemSystemEmployees,
    NavigationItemSystemIntegrations,
    NavigationItemTenants,
    NavigationItemUrlGenerator,
    NavigationItemV2Patients,
    NavigationItemV2Templates,
    NavigationItemV2Settings,
    NavigationMarketingPushNotification,
    NavigationMarketingPushNotificationHistory,
    NavigationReport,
    NavigationReportAppointments,
    NavigationReportAttendances,
} from "./navigation";
import { SubscriptionPermissionName } from "@app/wapps/system/subscription/permissions-name";
import { PermissionName } from "@app/wapps/core/permissions-name";
import { HealthFeatures } from "@app/wapps/health/feature-names";
import { Router } from "@angular/router";
import { CoreFeatures } from "@app/wapps/core/feature-names";
import { FinancialConfigService } from "@app/wapps/financial/financial-config.service";
import { HealthPermissionNames } from "@app/wapps/health/permissions-names";
import { isTranscriptionEnabledForTenant } from "@vhealth/transcription/eligibility";
import { IntegrationAccessService } from "@app/wapps/system/integration/integration-access.service";

@Injectable()
export class NavigationBuilderService {
    private _appSession = inject(AppSessionService);

    private _router = inject(Router);

    private _financialConfigService = inject(FinancialConfigService);

    private _integrationAccess = inject(IntegrationAccessService);

    private readonly ENGAGE_EDITIONS = new Set<string>([
        EditionNames.HealthEngagePro,
        EditionNames.HealthEngagePremium,
        EditionNames.HealthEngageStarter,
    ]);

    public buildNavigation(tenantInfo): FuseNavigation[] {
        const navigation: FuseNavigation[] = [
            {
                id: "applications",
                title: "Painel",
                type: "group",
                icon: "apps",
                children: [],
            },
        ];

        const nav = navigation[0];

        if (!this._appSession.tenantId) {
            this.buildHostNavigation(nav);
            return navigation;
        }

        switch (abp.setting.get(AppConsts.Settings.ApplicationVersion)) {
            case "2.0":
                this.buildV2Navigation(nav);
                break;

            default:
                this.buildDefaultNavigation(nav);

                const isEngageEdition = this.ENGAGE_EDITIONS.has(tenantInfo.edition?.name);
                if (isEngageEdition) {
                    const mobile = nav.children.find((i) => i.id === "mobile-app");
                    if (mobile) {
                        nav.children = [
                            nav.children[0],
                            mobile,
                            ...nav.children.filter((i) => i.id !== "mobile-app" && i !== nav.children[0]),
                        ];
                    }
                }
                break;
        }

        return navigation;
    }

    public buildHostNavigation(nav: FuseNavigation): void {
        nav.children.push(NavigationItemGlobalStats);
        nav.children.push(NavigationItemTenants);
        if (abp.auth.isGranted(SubscriptionPermissionName.PaymentLink)) {
            nav.children.push(NavigationItemUrlGenerator);
        }
        this._router.navigate(["stats"]).then();
        return;
    }

    buildV2Navigation(nav: FuseNavigation): void {
        if (
            abp.auth.isGranted(PermissionName.Patients) ||
            abp.auth.isGranted(PermissionName.PatientsQuery) ||
            abp.auth.isGranted(PermissionName.PatientsCreate) ||
            abp.auth.isGranted(PermissionName.PatientsUpdate) ||
            abp.auth.isGranted(PermissionName.PatientsDelete)
        ) {
            nav.children.push(NavigationItemV2Patients);
        }
        if (abp.features.isEnabled(HealthFeatures.Engage)) {
            nav.children.push(NavigationItemV2Templates);
        }
        nav.children.push(NavigationItemV2Settings);
    }

    buildDefaultNavigation(nav: FuseNavigation): void {
        if (
            abp.features.isEnabled(HealthFeatures.Dashboard) &&
            (abp.auth.isGranted(PermissionName.Appointments) ||
                abp.auth.isGranted(PermissionName.AppointmentsQueryAll) ||
                abp.auth.isGranted(PermissionName.AppointmentsQueryMine) ||
                abp.auth.isGranted(PermissionName.AppointmentsCreate))
        ) {
            nav.children.push(NavigationDashboardItem);
        }

        if (
            abp.features.isEnabled(HealthFeatures.Schedule) &&
            (abp.auth.isGranted(PermissionName.Appointments) ||
                abp.auth.isGranted(PermissionName.AppointmentsQueryAll) ||
                abp.auth.isGranted(PermissionName.AppointmentsQueryMine) ||
                abp.auth.isGranted(PermissionName.AppointmentsCreate))
        ) {
            nav.children.push(NavigationItemScheduleCalendar);
        }

        if (
            abp.auth.isGranted(PermissionName.Patients) ||
            abp.auth.isGranted(PermissionName.PatientsQuery) ||
            abp.auth.isGranted(PermissionName.PatientsCreate) ||
            abp.auth.isGranted(PermissionName.PatientsUpdate) ||
            abp.auth.isGranted(PermissionName.PatientsDelete)
        ) {
            nav.children.push(NavigationItemHealthPatients);
        }

        if (
            abp.features.isEnabled(CoreFeatures.VPay) &&
            this._appSession.isAdmin()
            //    || this.appSession.isDoctor()
        ) {
            nav.children.push(NavigationItemPay);
        }

        if (this._financialConfigService.isMenuEnabled()) {
            const menu = NavigationFinancial;
            if (this._financialConfigService.allowAccessConfig()) {
                menu.children.push(NavigationFinancialConfig);
            }

            if (this._financialConfigService.allowAccessRevenues()) {
                menu.children.push(NavigationFinancialRevenues);
            }

            if (this._financialConfigService.allowAccessExpenses()) {
                menu.children.push(NavigationFinancialExpenses);
            }

            if (this._financialConfigService.allowAccessReports()) {
                menu.children.push(NavigationFinancialCashFlow);
            }

            nav.children.push(menu);
        }

        if (abp.features.isEnabled(CoreFeatures.Report) && abp.auth.isGranted(PermissionName.Report)) {
            const menu = NavigationReport;
            if (
                abp.features.isEnabled(HealthFeatures.ReportSchedules) &&
                abp.auth.isGranted(PermissionName.ReportAppointmentQuery)
            ) {
                menu.children.push(NavigationReportAppointments);
            }

            if (
                abp.features.isEnabled(HealthFeatures.ReportAttendance) &&
                abp.auth.isGranted(PermissionName.ReportAppointmentQuery)
            ) {
                menu.children.push(NavigationReportAttendances);
            }

            nav.children.push(menu);
        }

        if (
            (abp.features.isEnabled(AppConsts.Features.FeaturePushNotification) ||
                abp.features.isEnabled(AppConsts.Features.FeaturePushNotificationLegacy)) &&
            abp.auth.isGranted(PermissionName.Marketing)
        ) {
            const menu = NavigationItemMarketingManagement;
            if (abp.auth.isGranted(PermissionName.MarketingPushNotificationCreate)) {
                menu.children.push(NavigationMarketingPushNotification);
            }

            if (abp.auth.isGranted(PermissionName.MarketingPushNotificationHistory)) {
                menu.children.push(NavigationMarketingPushNotificationHistory);
            }

            nav.children.push(menu);
        }

        if (
            (abp.features.isEnabled(AppConsts.Features.MobileAppBuilder) ||
                abp.features.isEnabled(AppConsts.Features.Pwa)) &&
            abp.auth.isGranted(PermissionName.Wizard)
        ) {
            const menu = NavigationItemAppManagement;
            if (abp.auth.isGranted(PermissionName.WizardDesignQuery)) {
                menu.children.push(NavigationAppManagementSettings);
            }

            if (abp.auth.isGranted(PermissionName.WizardModulesQuery)) {
                menu.children.push(NavigationAppManagementModules);
            }

            nav.children.push(menu);
        }

        if (
            (abp.auth.isGranted(HealthPermissionNames.ProceduresPermissionQueryMine) ||
                this._appSession.isUserInRoleTenant(AppConsts.RolesNames.Doctor)) &&
            abp.features.isEnabled(HealthFeatures.Schedule)
        ) {
            nav.children.push(NavigationConfigProfessionalProcedures);
        }

        const settingsItem = NavigationItemSettings;
        settingsItem.children.push(NavigationItemProfile);

        if (this._appSession.isAdmin()) {
            settingsItem.children.push(NavigationConfigGeneralInfo);
        }

        const globalSettingsItem = NavigationItemClinicSettings;
        if (
            this._appSession.isAdmin() &&
            abp.auth.isAnyGranted(
                HealthPermissionNames.ProceduresPermission,
                HealthPermissionNames.InsurancePermission,
                HealthPermissionNames.CareUnitsPermission,
                PermissionName.Users
            )
        ) {
            if (
                abp.auth.isAnyGranted(HealthPermissionNames.InsurancePermissionQuery) &&
                abp.features.isEnabled(HealthFeatures.Schedule)
            ) {
                globalSettingsItem.children.push(NavigationConfigHealthInsurance);
            }

            if (
                abp.auth.isAnyGranted(
                    HealthPermissionNames.CareUnitsPermission,
                    HealthPermissionNames.CareUnitsPermissionQuery
                )
            ) {
                globalSettingsItem.children.push(NavigationConfigHealthCareUnits);
            }

            if (abp.auth.isAnyGranted(PermissionName.Doctors, PermissionName.DoctorsQuery)) {
                globalSettingsItem.children.push(NavigationItemHealthDoctors);
            }

            if (abp.auth.isAnyGranted(PermissionName.Users, PermissionName.UsersQuery)) {
                globalSettingsItem.children.push(NavigationItemSystemEmployees);
            }

            if (
                abp.auth.isAnyGranted(
                    HealthPermissionNames.ProceduresPermission,
                    HealthPermissionNames.ProceduresPermissionQuery
                ) &&
                abp.features.isEnabled(HealthFeatures.Schedule)
            ) {
                globalSettingsItem.children.push(NavigationConfigHealthProcedures);
            }

            settingsItem.children.push(globalSettingsItem);
        }

        if (this._integrationAccess.isMenuEnabled()) {
            settingsItem.children.push(NavigationItemSystemIntegrations);
        }

        if (this._appSession.isAdmin()) {
            settingsItem.children.push(NavigationConfigPatient);
        }

        if (abp.features.isEnabled(HealthFeatures.Schedule) && this._appSession.isAdmin()) {
            settingsItem.children.push(NavigationConfigSchedule);
        }

        if (
            (abp.features.isEnabled(HealthFeatures.MedicalRecord) ||
                (HealthFeatures.isSmartPrescriptionEnabled() && !HealthFeatures.Engage)) &&
            this._appSession.isAdmin()
        ) {
            settingsItem.children.push(NavigationConfigMedicalRecord);
        }

        if (abp.features.isEnabled(HealthFeatures.Engage) && this._appSession.isAdmin()) {
            settingsItem.children.push(NavigationConfigHeathEngage);
        }

        if (
            (abp.features.isEnabled(HealthFeatures.MedicalRecord) ||
                abp.features.isEnabled(HealthFeatures.PregnancyCard)) &&
            this._appSession.isAdmin()
        ) {
            settingsItem.children.push(NavigationConfigChildGrowthCurve);
        }

        if (
            //abp.features.isEnabled(HealthFeatures.FeatureFiles) &&
            this._appSession.isAdmin() ||
            this._appSession.isDoctor()
        ) {
            settingsItem.children.push(NavigationConfigDocumentTemplates);
        }

        if (
            (this._appSession.isAdmin() || this._appSession.isDoctor()) &&
            abp.setting.getBoolean(HealthFeatures.TranscriptionSettingsPage) &&
            isTranscriptionEnabledForTenant(this._appSession.tenantInfo?.paymentGateway)
        ) {
            settingsItem.children.push(NavigationConfigTranscription);
        }

        nav.children.push(settingsItem);
    }
}
